import { useState, useEffect, useRef, useCallback } from "react";
import { Table, Spinner } from "react-bootstrap";
import { useGetAllclassrooms } from "../../../../hooks/teacher/classroom/useGetAllClassrooms";
import QuestSelectorSkeleton from "./QuestSelectorSkeleton";
import TableError from "../../common/TableError";

import "../classroom/SelectStudents.css";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";

const PER_PAGE = ITEMS_PER_PAGE;

/**
 * Table-based classroom selector with infinite scroll, search, and create functionality.
 * Modeled after QuestSelector.
 *
 * @param {Object} props
 * @param {Array} props.assignedClasses - Currently assigned classrooms [{id, name, dueDate}]
 * @param {Function} props.setAssignedClasses - Setter for assignedClasses state
 * @param {string|null} props.teacherId - The ID of the teacher
 * @param {"School"|"Teacher"} props.type - The type of user
 */
const ClassroomSelector = ({ assignedClasses, setAssignedClasses, teacherId, type }) => {
  // Classroom list state (accumulated for infinite scroll)
  const [allClassrooms, setAllClassrooms] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef(null);

  // Hooks
  const { classrooms, meta, loading, error, getAllClassrooms } =
    useGetAllclassrooms();

  // Scroll container ref
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Fetch classrooms
  const fetchClassrooms = useCallback(
    (pageNum, search = "") => {
      const params = {
        page: pageNum,
        per_page: PER_PAGE,
      };
      if (search.trim()) {
        params.filters = { name: search.trim() };
      }
      if (type === "School" && teacherId) {
        params.teacher_id = teacherId;
      }
      getAllClassrooms(params);
    },
    [getAllClassrooms],
  );

  // Initial fetch
  useEffect(() => {
    fetchClassrooms(1, "");
  }, []);

  // Process fetched classrooms
  useEffect(() => {
    if (classrooms === undefined || classrooms === null) return;

    if (page === 1) {
      setAllClassrooms(classrooms);
    } else {
      setAllClassrooms((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const newItems = classrooms.filter((c) => !existingIds.has(c.id));
        return [...prev, ...newItems];
      });
    }

    // Check if there are more pages
    if (meta) {
      setHasMore(meta.current_page < meta.last_page);
    } else {
      setHasMore(false);
    }

    setInitialLoad(false);
  }, [classrooms, meta]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchClassrooms(nextPage, searchTerm);
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.1,
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, loading, page, searchTerm, fetchClassrooms]);

  // Handle search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce search API call
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setAllClassrooms([]);
      setHasMore(true);
      fetchClassrooms(1, value);
    }, 400);
  };

  // Toggle classroom selection
  const handleToggleClassroom = (classroom) => {
    const isAssigned = assignedClasses.some((c) => c.id === classroom.id);

    if (isAssigned) {
      setAssignedClasses(assignedClasses.filter((c) => c.id !== classroom.id));
    } else {
      setAssignedClasses([
        ...assignedClasses,
        {
          id: classroom.id,
          name: classroom.name,
          dueDate: new Date().toISOString().split("T")[0],
        },
      ]);
    }
  };

  // Toggle all visible classrooms
  const handleToggleAll = () => {
    const assignedIds = new Set(assignedClasses.map((c) => c.id));
    const allVisibleSelected =
      allClassrooms.length > 0 &&
      allClassrooms.every((c) => assignedIds.has(c.id));

    if (allVisibleSelected) {
      // Deselect all visible
      const visibleIds = new Set(allClassrooms.map((c) => c.id));
      setAssignedClasses(assignedClasses.filter((c) => !visibleIds.has(c.id)));
    } else {
      // Select all visible (add missing ones)
      const toAdd = allClassrooms
        .filter((c) => !assignedIds.has(c.id))
        .map((c) => ({
          id: c.id,
          name: c.name,
          dueDate: new Date().toISOString().split("T")[0],
        }));
      setAssignedClasses([...assignedClasses, ...toAdd]);
    }
  };

  const assignedIds = new Set(assignedClasses.map((c) => c.id));
  const isAllSelected =
    allClassrooms.length > 0 &&
    allClassrooms.every((c) => assignedIds.has(c.id));

  // Initial loading state
  if (initialLoad && loading) {
    return (
      <div>
        <h5 className="fw-semibold mb-3">Assign Lesson to Classrooms</h5>
        <div className="custom-table-container">
          <div className="custom-table-wrapper select-student-table-scroll">
            <QuestSelectorSkeleton rows={5} />
          </div>
        </div>
      </div>
    );
  }

  // Error state (only on initial load)
  if (error && allClassrooms.length === 0) {
    return (
      <div>
        <h5 className="fw-semibold mb-3">Assign Lesson to Classrooms</h5>
        <TableError
          error={error}
          handleRefresh={() => {
            setPage(1);
            setAllClassrooms([]);
            fetchClassrooms(1, searchTerm);
          }}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div>
      <h5 className="fw-semibold mb-3">Assign Lesson to Classrooms</h5>

      {/* Search & Create Row */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <div className="student-selector-search flex-grow-1">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="student-selector-search-input"
            placeholder="Search classrooms..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Classroom Table */}
      <div className="custom-table-container">
        <div
          className="custom-table-wrapper select-student-table-scroll"
          ref={scrollContainerRef}
        >
          <Table hover className="custom-table mb-0">
            <thead className="custom-table-head">
              <tr
                style={{
                  backgroundColor: "#cfebff",
                  color: "#495057",
                  fontWeight: "500",
                }}
              >
                <th
                  style={{
                    width: "60px",
                    verticalAlign: "middle",
                    padding: "12px 16px",
                    paddingLeft: "30px",
                  }}
                >
                  <div onClick={handleToggleAll} style={{ cursor: "pointer" }}>
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      style={{
                        width: "1.2rem",
                        height: "1.2rem",
                      }}
                    />
                  </div>
                </th>
                <th style={{ fontSize: "1.1rem" }}>
                  Classrooms
                  {assignedClasses.length > 0 && (
                    <span
                      className="ms-2 text-primary"
                      style={{ fontSize: "0.9rem", fontWeight: "600" }}
                    >
                      ({assignedClasses.length} Selected)
                    </span>
                  )}
                </th>
              </tr>
            </thead>

            <tbody>
              {allClassrooms.length === 0 && !loading ? (
                <tr>
                  <td colSpan="2" style={{ textAlign: "center" }}>
                    {searchTerm
                      ? "No classrooms found matching your search"
                      : "No classrooms available"}
                  </td>
                </tr>
              ) : (
                <>
                  {allClassrooms.map((classroom) => (
                    <tr
                      key={classroom.id}
                      onClick={() => handleToggleClassroom(classroom)}
                      style={{ cursor: "pointer" }}
                    >
                      <td
                        style={{
                          width: "60px",
                          verticalAlign: "middle",
                          padding: "12px 16px",
                          paddingLeft: "30px",
                        }}
                      >
                        <div>
                          <input
                            className="form-check-input m-0"
                            type="checkbox"
                            checked={assignedIds.has(classroom.id)}
                            onChange={() => handleToggleClassroom(classroom)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: "1.2rem",
                              height: "1.2rem",
                            }}
                          />
                        </div>
                      </td>
                      <td
                        className="custom-name"
                        style={{
                          verticalAlign: "middle",
                          padding: "12px 16px",
                        }}
                      >
                        {classroom.name}
                      </td>
                    </tr>
                  ))}

                  {/* Sentinel for infinite scroll */}
                  <tr ref={sentinelRef}>
                    <td
                      colSpan="2"
                      style={{
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      {loading && (
                        <Spinner
                          animation="border"
                          size="sm"
                          variant="primary"
                        />
                      )}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ClassroomSelector;
